package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.graphics.ColorFilter
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.MerchantProfileDetailsViewModel
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.model.IconData
import com.example.mystybag.common.ui.components.custom.CircleIconView
import com.example.mystybag.common.ui.components.custom.ImageDescriptionView
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.appCore.logic.PhoneSelectionBottomSheetChild
import com.example.mystybag.appCore.views.PhoneSelectionBottomSheetView
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.logic.TextInputBottomSheetChild
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.common.views.CommonBottomSheetFactory
import com.example.mystybag.common.views.TextInputBottomSheetView
import com.example.mystybag.core.model.IconDescription
import com.example.mystybag.core.model.ImageDescription
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p124
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.ColorDefines
import com.example.mystybag.core.views.SharedColor
import dev.icerock.moko.resources.compose.painterResource
import androidx.compose.foundation.Image
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp

// Figma: Merchant profile – 412x917, cornerRadius 48
// Header: padding top 12, bottom 20, itemSpacing 16; back 48x48, title 252x24
// Avatar: 124x124, Add Photo 48x48
// List: 380px width (padding 16), section header + 1px line (spacing 12), list items 60dp, padding top 24 bottom 12, itemSpacing 24 between rows, 8 between label/value/chevron

@Composable
fun MerchantProfileDetailsView(viewModel: MerchantProfileDetailsViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val keyboardController = LocalSoftwareKeyboardController.current

    if (rememberIsWebDesktopLayout()) {
        MerchantProfileDetailsWebView(viewModel = viewModel, uiState = uiState)
        return
    }

    BottomSheetUIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = true,
        bottomSheetFactory = {
            when (it) {
                is TextInputBottomSheetChild -> TextInputBottomSheetView(it.viewModel)
                is PhoneSelectionBottomSheetChild -> PhoneSelectionBottomSheetView(it.viewModel)
                else -> CommonBottomSheetFactory(it)
            }
        },
        navigationContent = {
            // Frame 135372: padding top 12, bottom 20; title centered on screen
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = p16, end = p16, top = p12, bottom = p20)
            ) {
                // Title centered in full width
                Texts.TitleMedium(
                    text = sharedString(SR.strings.merchant_profile),
                    color = AppTheme.current.colors.onSurface,
                    modifier = Modifier
                        .fillMaxWidth()
                        .align(Alignment.Center),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                // Back button 48x48 on the left
                Box(
                    modifier = Modifier
                        .size(p48)
                        .align(Alignment.CenterStart)
                        .clip(CircleShape)
                        .background(AppTheme.current.colors.surfaceContainerLowest)
                        .noRippleClickable {
                            keyboardController?.hide()
                            viewModel.navCallbacks.backButtonCallback()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.arrow_back),
                        contentDescription = null,
                        contentScale = ContentScale.Fit,
                        modifier = Modifier.size(width = LocalTheme.p10, height = LocalTheme.p18),
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
                    )
                }
            }
        }
    ) { state ->
        MerchantProfileDetailsContent(uiState = uiState, viewModel = viewModel, modifier = Modifier.fillMaxSize())
    }
}

@Composable
private fun MerchantProfileDetailsWebView(
    viewModel: MerchantProfileDetailsViewModel,
    uiState: com.example.mystybag.appCore.model.pages.MerchantProfileDetailsUIState
) {
    BottomSheetUIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = false,
        navigationContent = null,
        bottomSheetFactory = {
            when (it) {
                is TextInputBottomSheetChild -> TextInputBottomSheetView(it.viewModel)
                is PhoneSelectionBottomSheetChild -> PhoneSelectionBottomSheetView(it.viewModel)
                else -> CommonBottomSheetFactory(it)
            }
        }
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            ClientWebHeaderBar(
                selectedTab = 3,
                homeTabIndex = 0,
                deliveryTabIndex = 1,
                favoritesTabIndex = 2,
                menuTabIndex = 3,
                searchCallbacks = null,
                onFilterClick = {},
                showSearchAndFilter = false,
                showBackButton = true,
                onBackClick = { viewModel.navCallbacks.backButtonCallback() },
                centerTitle = sharedString(SR.strings.merchant_profile)
            )
            WebHeaderBottomDivider()
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
                contentAlignment = Alignment.TopCenter
            ) {
                MerchantProfileDetailsContent(
                    uiState = uiState,
                    viewModel = viewModel,
                    modifier = Modifier
                        .width(1080.dp)
                        .fillMaxHeight()
                        .padding(horizontal = p16, vertical = p8)
                )
            }
        }
    }
}

@Composable
private fun MerchantProfileDetailsContent(
    uiState: com.example.mystybag.appCore.model.pages.MerchantProfileDetailsUIState,
    viewModel: MerchantProfileDetailsViewModel,
    modifier: Modifier
) {
    Box(modifier = modifier) {
        when {
            uiState.isLoading -> {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
            }
            else -> {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .scrollEdgeFade(showTop = false)
                        .padding(horizontal = p16), // 412 - 380 = 32, 16 each side
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Group 30: Avatar 124x124, Add Photo Icon 48x48
                    Box(
                        modifier = Modifier
                            .size(p124)
                            .noRippleClickable(viewModel::onLogoButtonClicked),
                        contentAlignment = Alignment.BottomEnd
                    ) {
                        ImageDescriptionView(
                            modifier = Modifier
                                .fillMaxSize()
                                .clip(CircleShape),
                            image = if (uiState.logoImageFileName != null) {
                                val imagePath = com.example.mystybag.cropImage.expected.getTempImagePath(uiState.logoImageFileName!!)
                                ImageDescription(link = imagePath, icon = null)
                            } else {
                                val imageUrl = uiState.logoImageUrl?.trim()
                                if (!imageUrl.isNullOrBlank()) {
                                    ImageDescription(link = imageUrl, icon = null)
                                } else {
                                    ImageDescription(
                                        link = null,
                                        icon = IconDescription(
                                            resource = SRC.images.avatar_fill_icon,
                                            imageSize = p36,
                                            background = SharedColor(color = ColorDefines.SurfaceContainerHighest)
                                        )
                                    )
                                }
                            }
                        )
                        CircleIconView(
                            modifier = Modifier.border(p1, AppTheme.current.colors.surfaceDim, CircleShape),
                            model = IconData(
                                circleSize = p48,
                                iconSize = p24,
                                icon = painterResource(SRC.images.camera_outlined_icon)
                            )
                        )
                    }

                    // List Group: 380px width, itemSpacing 24
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = p24),
                        verticalArrangement = Arrangement.spacedBy(p12)
                    ) {
                        // Section Header: label 16px + 12dp spacing + 1px line
                        MerchantProfileSectionHeader(
                            title = sharedString(SR.strings.section_account)
                        )

                        // List items: 60dp height, padding top 24 bottom 12, itemSpacing 8
                        MerchantProfileListItem(
                            label = sharedString(SR.strings.email),
                            value = uiState.email,
                            onClick = null,
                            showChevron = false
                        )
                        MerchantProfileListItem(
                            label = sharedString(SR.strings.company_name),
                            value = uiState.companyName,
                            onClick = viewModel::onCompanyNameRowClick
                        )
                        MerchantProfileListItem(
                            label = sharedString(SR.strings.phone_number),
                            value = if (uiState.dialCode.isNotEmpty() || uiState.phoneNumber.isNotEmpty())
                                "${uiState.dialCode} ${uiState.phoneNumber}".trim()
                            else
                                "",
                            onClick = viewModel::onPhoneRowClick
                        )
                        MerchantProfileListItem(
                            label = sharedString(SR.strings.bulstat),
                            value = uiState.bulstat,
                            onClick = viewModel::onBulstatRowClick
                        )
                        MerchantProfileListItem(
                            label = sharedString(SR.strings.iban),
                            value = uiState.iban,
                            onClick = viewModel::onIbanRowClick
                        )
                        MerchantProfileListItem(
                            label = sharedString(SR.strings.address),
                            value = uiState.companyStreet,
                            onClick = viewModel::onCompanyStreetRowClick
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MerchantProfileSectionHeader(title: String) {
    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        Texts.LabelLarge(
            text = title.uppercase(),
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier
                .fillMaxWidth()
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(p1)
                .background(AppTheme.current.colors.outline)
        )
    }
}

@Composable
private fun MerchantProfileListItem(
    label: String,
    value: String,
    onClick: (() -> Unit)? = null,
    showChevron: Boolean = true
) {
    val rowModifier = Modifier
        .fillMaxWidth()
        .height(p48)
        .then(
            if (onClick != null) Modifier.rippleClick(padding = p0, roundPadding = p8, action = onClick)
            else Modifier
        )
    Row(
        modifier = rowModifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p8)
    ) {
        Texts.TitleMedium(
            text = label,
            color = AppTheme.current.colors.onSurface
        )
        Texts.BodyMedium(
            text = value.ifEmpty { "—" },
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier.weight(1f).padding(end = p12),
            textAlign = TextAlign.Right
        )
        if (showChevron) {
            Image(
                painter = painterResource(SRC.images.right_arrow),
                contentDescription = null,
                contentScale = ContentScale.Fit,
                modifier = Modifier.size(width = p8, height = LocalTheme.p14),
                colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
            )
        } else {
            // Reserve same space as arrow so value aligns with other rows
            Spacer(modifier = Modifier.size(width = p8, height = LocalTheme.p14))
        }
    }
}
